import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const KEY = "mb300_consent";
const MAX_AGE = 1000 * 60 * 60 * 24 * 182;

const readConsent = () => {
  try {
    const c = JSON.parse(localStorage.getItem(KEY));
    return c && Date.now() - c.ts < MAX_AGE ? c : null;
  } catch {
    return null;
  }
};

// Non-essential scripts must only be injected from here, after explicit consent.
const applyConsent = (c) => {
  window.mb300Consent = c;
  window.dispatchEvent(new CustomEvent("mb300:consent", { detail: c }));
};

export const CookieConsent = () => {
  const { t } = useI18n();
  const c = t.cookies;
  const [consent, setConsent] = useState(readConsent);
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (consent) applyConsent(consent);
  }, [consent]);

  useEffect(() => {
    const on = () => {
      const cur = readConsent();
      setPrefs({ analytics: !!cur?.analytics, marketing: !!cur?.marketing });
      setOpen(true);
    };
    window.addEventListener("mb300:open-cookies", on);
    return () => window.removeEventListener("mb300:open-cookies", on);
  }, []);

  const save = (p) => {
    const v = { necessary: true, analytics: !!p.analytics, marketing: !!p.marketing, ts: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(v));
    setConsent(v);
    setOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {!consent && (
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1, transition: { delay: 1.2, duration: 0.6 } }} exit={{ y: 40, opacity: 0, transition: { duration: 0.25 } }}
            className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-3xl border border-white/15 bg-[#0B1530]/95 p-5 backdrop-blur-xl md:p-6" data-testid="cookie-banner">
            <p className="text-sm text-[var(--steel)]">
              {c.text} <Link to="/cookies" className="text-[var(--gold)] underline underline-offset-4" data-testid="cookie-policy-link">{c.policy}</Link>
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={() => save({ analytics: true, marketing: true })} data-testid="cookie-accept" className="bg-[var(--gold)] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--night)] hover:bg-[var(--gold-hi)]">{c.accept}</button>
              <button onClick={() => save({ analytics: false, marketing: false })} data-testid="cookie-refuse" className="border border-white/30 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-white hover:border-white">{c.refuse}</button>
              <button onClick={() => setOpen(true)} data-testid="cookie-settings" className="px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--steel)] underline underline-offset-4 hover:text-white">{c.settings}</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-white/15 bg-[#0B1530] text-white" data-lenis-prevent data-testid="cookie-settings-dialog">
          <DialogTitle className="font-display text-3xl uppercase">{c.title}</DialogTitle>
          <DialogDescription className="text-[var(--steel-dim)]">{c.text}</DialogDescription>
          <div className="mt-2 divide-y divide-white/10">
            {["necessary", "analytics", "marketing"].map((k) => (
              <div key={k} className="flex items-start justify-between gap-6 py-4">
                <div>
                  <p className="font-semibold">{c.cats[k].t}</p>
                  <p className="mt-1 text-sm text-[var(--steel-dim)]">{c.cats[k].d}</p>
                </div>
                {k === "necessary" ? (
                  <span className="shrink-0 font-mono text-[11px] uppercase text-[var(--gold)]">{c.always}</span>
                ) : (
                  <Switch checked={prefs[k]} onCheckedChange={(v) => setPrefs((p) => ({ ...p, [k]: v }))} data-testid={`cookie-toggle-${k}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <button onClick={() => save(prefs)} data-testid="cookie-save" className="bg-[var(--gold)] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--night)]">{c.save}</button>
            <button onClick={() => save({})} data-testid="cookie-refuse-all" className="border border-white/30 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em]">{c.refuse}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
